import React, { createContext, useContext, useEffect, useState } from "react";
import { io } from "socket.io-client";

/**
 * Resolve Socket.IO server URL (origin only). Uses same origin as API so cookies are sent.
 */
function getSocketUrl() {
  const base = process.env.REACT_APP_SERVER_URL || "";
  if (base.startsWith("http://") || base.startsWith("https://")) {
    try {
      return new URL(base).origin;
    } catch {
      return window.location.origin;
    }
  }
  return window.location.origin;
}

const SocketContext = createContext(null);

export function SocketProvider({ children }) {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const url = getSocketUrl();
    const instance = io(url, {
      withCredentials: true,
      autoConnect: true,
    });

    instance.on("connect", () => setConnected(true));
    instance.on("disconnect", () => setConnected(false));

    setSocket(instance);

    return () => {
      instance.removeAllListeners();
      instance.close();
      setSocket(null);
      setConnected(false);
    };
  }, []);

  const value = socket ? { socket, connected } : { socket: null, connected: false };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const ctx = useContext(SocketContext);
  if (!ctx) {
    throw new Error("useSocket must be used within SocketProvider");
  }
  return ctx;
}
